import { DateTime } from 'luxon';
import { ChatMuteIcon } from '../../../assets/svg';
import { TruncatedText } from '../../../styles/styles';
import { Disclaimer, P } from '../../../styles/text';
import { lightTheme } from '../../../styles/theme';
import { ConversationType } from '../../../types/conversation.types';
import { formatRelativeTime } from '../helpers/chatFormat';
import { ConversationAvatar } from './ConversationAvatar';
import {
  AvatarSlot,
  Content,
  Line,
  MentionBubble,
  NameSlot,
  QuietSlot,
  Row,
  Shrink,
  Trailing,
  UnreadBubble,
  WaitingChip,
} from './ConversationRow.styled';
import { GroupMembersTooltip } from './GroupMembersTooltip';

type Props = {
  /** Unique per row - it names the members tooltip. */
  id: number;
  name: string;
  imageUrl: string | null;
  type: ConversationType;
  lastMessageAt: string | null;
  /** The line under the name: the last message, or what stands in for one. */
  preview: string;
  unread: number;
  hasMention: boolean;
  mutedUntil: string | null;
  /** A group's members, for the tooltip on its avatar. */
  memberNames?: string[];
  /** A support thread nobody has answered yet. */
  waiting?: boolean;
  /** What the waiting chip says - a dispatcher thread waits for a person. */
  waitingLabel?: string;
  selected?: boolean;
  onClick: () => void;
};

/**
 * One conversation in a list: the admin inbox, and the merchant app's Groups
 * tab. Each caller turns its own row shape into these props - what a preview
 * says differs per app and per type, how the row looks does not.
 */
export const ConversationRow = ({
  id,
  name,
  imageUrl,
  type,
  lastMessageAt,
  preview,
  unread,
  hasMention,
  mutedUntil,
  memberNames,
  waiting,
  waitingLabel = 'Waiting',
  selected,
  onClick,
}: Props) => {
  const muted = !!mutedUntil && DateTime.fromISO(mutedUntil) > DateTime.now();

  /*
   * The row's two text weights, in whichever direction the surface runs. The
   * selected row is solid red, so its ink is white and its quiet text is white
   * held back - not lightGray, which sits at almost the same value as the red
   * and would leave the preview illegible.
   */
  const ink = selected ? lightTheme.white : lightTheme.darkGray;
  const quiet = selected ? `${lightTheme.white}B3` : lightTheme.lightGray;

  return (
    <Row $selected={selected} onClick={onClick}>
      <AvatarSlot>
        {/* Who is in there, before opening it - which is the one question a
            group row cannot answer by itself, and opening it to find out marks
            it read. The names come from the row's own `participants` string,
            so this costs no request. */}
        <GroupMembersTooltip
          id={`chat-group-members-${id}`}
          names={memberNames}
        >
          <ConversationAvatar
            name={name}
            imageUrl={imageUrl}
            type={type}
            size={36}
            inverted={selected}
          />
        </GroupMembersTooltip>
      </AvatarSlot>

      <Content>
        <Line>
          <Shrink>
            <NameSlot>
              <P $m="0" $bold $color={ink}>
                <TruncatedText color={ink}>{name}</TruncatedText>
              </P>
            </NameSlot>
          </Shrink>

          <Trailing>
            {waiting && (
              <WaitingChip $onPrimary={selected}>{waitingLabel}</WaitingChip>
            )}
            <QuietSlot>
              <Disclaimer $m="0" $color={quiet}>
                {formatRelativeTime(lastMessageAt)}
              </Disclaimer>
            </QuietSlot>
          </Trailing>
        </Line>

        <Line>
          <Shrink>
            <QuietSlot>
              <Disclaimer $m="0">
                <TruncatedText color={quiet}>{preview}</TruncatedText>
              </Disclaimer>
            </QuietSlot>
          </Shrink>

          <Trailing>
            {muted && <ChatMuteIcon color={quiet} />}
            {/* Left of the count, the way every messenger orders these: the
                count is the row's usual furniture, so the marker reads as
                something added to it rather than as a second number. */}
            {hasMention && (
              <MentionBubble $onPrimary={selected}>@</MentionBubble>
            )}
            {unread > 0 && (
              <UnreadBubble $onPrimary={selected}>
                {unread > 99 ? '99+' : unread}
              </UnreadBubble>
            )}
          </Trailing>
        </Line>
      </Content>
    </Row>
  );
};

/**
 * A group's members from the comma-joined `participants` string a list row
 * carries - the whole list available without fetching the conversation.
 */
export const participantNames = (participants: string | null) =>
  (participants || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
